import React, { useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { contactInfo, departments } from '@/data/contactData';
import { MapPin, Phone, Mail, MessageCircleQuestionIcon, Facebook, Instagram, Twitter, Linkedin, Youtube, Smartphone, Apple, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useDispatch } from 'react-redux';
import { submitContact } from '@/store/slices/contactSlice';

// TODO: update this to the actual hosted path of sendmail.php on your server
const SENDMAIL_URL = 'https://prayasinstitute.com/sendmail/sendmail.php';



const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);


  const dispatch = useDispatch();




  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic Validation
    if (formData.phone.length !== 10) {
      toast.error('Phone number must be exactly 10 digits.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    if (formData.name.trim().length < 3) {
      toast.error('Name must be at least 3 characters.');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append('type', 'contact');
      payload.append('name', formData.name);
      payload.append('phone', formData.phone);
      payload.append('mail', formData.email); // sendmail.php reads $_POST['mail']
      payload.append('message', `Subject: ${formData.subject}\n\n${formData.message}`);

      const res = await fetch(SENDMAIL_URL, {
        method: 'POST',
        body: payload,
      });

      const data = await res.json();

      // Naya backend contact API bhi hit karo (parallel, sendmail ke response pe depend nahi)
      dispatch(submitContact({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message
      })).catch(() => {
        // silent fail — UI ka success/error message sendmail.php ke response pe hi based rahega
      });

      if (data.success) {
        toast.success('Message sent successfully! We will get back to you soon.');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        toast.error(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      toast.error('Unable to send message. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  const contactCards = [
    {
      icon: MapPin,
      title: 'Visit Us',
      content: `${contactInfo.address.line1}, ${contactInfo.address.line2}, ${contactInfo.address.city}`,
      link: "https://www.google.com/maps?sca_esv=cf5c25c52e0d4239&rlz=1CDGOYI_enIN1168IN1168&hl=en-GB&sxsrf=ANbL-n6etY2GmzTJf8fwmCGTpm9cFy6CAw:1770281869602&kgmid=/g/11n0wqxbf7&shndl=30&kgs=fa69f290ce53d438&shem=shrtsdl&um=1&ie=UTF-8&fb=1&gl=in&sa=X&geocode=Kc9UEJ8Bw2g5MSKlfLyICU9s&daddr=2nd+floor,+Hemu+kalani+circle,+Dariyani+mansion,+Sindhu+Nagar,+Shastri+Nagar,+Bhilwara,+Rajasthan+311001"
    },
    { icon: Phone, title: 'Call Us', content: contactInfo.phone.primary },
    { icon: Mail, title: 'Email Us', content: contactInfo.email.info },
    { icon: MessageCircleQuestionIcon, title: 'Admission Support', content: contactInfo.support.content }
  ];

  return (
    <div>
      <PageHeader
        title="Contact Us"
        subtitle="Get in touch with us for admissions, queries, or support"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Contact' }
        ]}
      />

      <section className="section-padding bg-background">
        <div className="container-custom">
          {/* Contact Info Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactCards.map((item, idx) => {
              const CardWrapper = item.link ? 'a' : 'div';

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <CardWrapper
                    {...(item.link && {
                      href: item.link,
                      target: "_blank",
                      rel: "noopener noreferrer"
                    })}
                    className="block h-full"
                  >
                    <Card className={`h-full text-center border-2 transition-all duration-300 hover:shadow-lg group ${item.link ? 'hover:border-primary cursor-pointer' : ''
                      }`}>
                      <CardContent className="p-6">
                        <div className="h-14 w-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                          <item.icon className="h-7 w-7 text-primary" />
                        </div>
                        <h3 className="font-heading font-bold text-foreground mb-2">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.content}</p>
                      </CardContent>
                    </Card>
                  </CardWrapper>
                </motion.div>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <Card>
              <CardContent className="p-8">
                <h3 className="text-xl font-heading font-bold text-foreground mb-6">
                  Connect With Us
                </h3>

                <div className="grid lg:grid-cols-2 gap-4">

                  <a href={contactInfo.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 border rounded-xl hover:border-primary hover:bg-primary/5 transition"
                  >
                    <Facebook className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Facebook – Prayas Institute</span>
                  </a>


                  <a href={contactInfo.social.facebookMaths}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 border rounded-xl hover:border-primary hover:bg-primary/5 transition"
                  >
                    <Facebook className="h-4 w-4 text-secondary" />
                    <span className="text-sm font-medium">Facebook – Mathematics Mantra</span>
                  </a>


                  <a href={contactInfo.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 border rounded-xl hover:border-primary hover:bg-primary/5 transition"
                  >
                    <Instagram className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Instagram – Prayas Institute</span>
                  </a>


                  <a href={contactInfo.social.instagramMaths}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 border rounded-xl hover:border-primary hover:bg-primary/5 transition"
                  >
                    <Instagram className="h-4 w-4 text-secondary" />
                    <span className="text-sm font-medium">Instagram – Mathematics Mantra</span>
                  </a>


                  <a href={contactInfo.social.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 border rounded-xl hover:border-primary hover:bg-primary/5 transition"
                  >
                    <Twitter className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Twitter (X)</span>
                  </a>


                  <a href={contactInfo.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 border rounded-xl hover:border-primary hover:bg-primary/5 transition"
                  >
                    <Linkedin className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">LinkedIn</span>
                  </a>


                  <a href={contactInfo.social.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 border rounded-xl hover:border-primary hover:bg-primary/5 transition"
                  >
                    <Youtube className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-medium">YouTube Channel</span>
                  </a>


                  <a href={contactInfo.social.androidApp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 border rounded-xl hover:border-primary hover:bg-primary/5 transition"
                  >
                    <Smartphone className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium">Android App (Play Store)</span>
                  </a >


                  <a href={contactInfo.social.iosApp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 border rounded-xl hover:border-primary hover:bg-primary/5 transition"
                  >
                    <Apple className="h-4 w-4 text-gray-800" />
                    <span className="text-sm font-medium">iOS App (App Store)</span>
                  </a >


                  <a href={contactInfo.social.onlineTestPortal}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 border rounded-xl hover:border-primary hover:bg-primary/5 transition"
                  >
                    <Globe className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium">Online Test Portal</span>
                  </a >
                </div >
              </CardContent >
            </Card >
            {/* Contact Form */}
            < motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="border-2">
                <CardContent className="p-8">
                  <h3 className="text-xl font-heading font-bold text-foreground mb-5">
                    Send us a Message
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-2">
                    <div>
                      <Label htmlFor="name">Your Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <Input
                          id="phone"
                          type="number"
                          inputMode="numeric"
                          pattern="[0-9]{10}"
                          maxLength={10}
                          value={formData.phone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 10) {
                              setFormData({ ...formData, phone: value });
                            }
                          }}
                          required
                          className="mt-2"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        rows={5}
                        className="mt-2"
                      />
                    </div>
                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-primary hover:bg-primary-dark text-primary-foreground disabled:opacity-60"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </ motion.div>
          </div >

          {/* Map & Departments */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <Card className="overflow-hidden">
              <iframe
                src={contactInfo.map}
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Institute Location"
              />
            </Card>

          </motion.div >
        </div >
      </section >
    </div >
  );
};

export default ContactPage;